'use client';

import Close from '@hc-frontend/core-icons/kit/close';
import { DialogCloseButton, Link } from '@hc-frontend/core-ui';
import { toUSPhone } from '@hc-frontend/core-utils-format';
import { CallCta } from '@hc-frontend/shells-shared';
import Image from 'next/image';
import type { ReactNode } from 'react';

import { AnimatedPhoneIcon } from '../../atoms/animated-phone-icon/animated-phone-icon';
import { ConnectMeButton } from '../../atoms/connect-me-button';
import { Countdown } from '../../atoms/countdown/countdown';
import { useUccModalDataContext } from './modal-data-provider';
import { useModalCommonUtils } from './use-modal-common-utils';

type UccModalContentMultipleOptionsProps = {
  customImage?: ReactNode;
  customHeading?: ReactNode;
  showCountdown?: boolean;
  uucButtonText?: string;
};

export function UccModalContentMultipleOptions({
  customImage,
  customHeading,
  showCountdown = true,
  uucButtonText,
}: UccModalContentMultipleOptionsProps) {
  const {
    phoneNumber,
    marketplaceStaticBaseUrl,
    primaryCampaign,
    secondaryCampaign,
    metricName,
    experience,
    sessionId,
    product,
  } = useUccModalDataContext();

  const {
    isOpen,
    isInitCall,
    internalRef,
    connectMeButtonId,
    clickToCallButtonId,
    clickToCallLinkId,
    connectMeDestinationPhoneNumber,
    handleOnClickBackdrop,
    handleOnClickClose,
    handleOnCountdownClose,
    handleOnClickConnectMe,
  } = useModalCommonUtils();

  const uucButton = uucButtonText ?? 'Start Call!';

  return (
    <div className="hc-imposter-layout">
      <dialog
        aria-modal="true"
        ref={internalRef}
        onClick={handleOnClickBackdrop}
        className="max-w-[500px] overflow-visible rounded-lg bg-gray-0 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
      >
        <div className="relative flex w-full justify-center overflow-visible">
          {customImage ?? (
            <>
              <div className="absolute left-1/2 top-0 aspect-square w-full max-w-[300px] -translate-x-1/2 -translate-y-1/2 tablet:max-w-[380px]">
                <Image
                  src={`${marketplaceStaticBaseUrl}/marketplace/images/call-licensed-agent-woman-dot.png`}
                  alt="Speak to a licensed agent"
                  fill
                />
              </div>
              <div className="aspect-[3/1] w-full max-w-[300px] tablet:aspect-[2/1]" />
            </>
          )}

          <DialogCloseButton
            className="absolute right-16-16 top-16-16 outline-none"
            onClick={handleOnClickClose}
          >
            <Close className="size-16-16 text-gray-200" />
          </DialogCloseButton>
        </div>
        <div className="px-24-24 py-32-32 text-center text-text-950">
          {customHeading ?? (
            <h1 className="hc-typography--heading-three mb-24-32 text-balance">
              A licensed insurance agent just became available!
            </h1>
          )}

          {phoneNumber ? (
            <>
              {!isInitCall ? (
                <p
                  className="hc-typography--paragraph-md mb-32-32 text-balance"
                  data-testid="initial-connect-me-message"
                >
                  To connect, click{' '}
                  <span className="font-bold">Connect Me!</span> and pick up the
                  phone when it rings.
                  {isOpen && showCountdown ? (
                    <>
                      {' '}
                      Your spot will go to another caller in{' '}
                      <span className="font-bold">
                        <Countdown
                          initialSeconds={45}
                          onEnd={handleOnCountdownClose}
                        />
                      </span>{' '}
                      seconds.
                    </>
                  ) : null}
                </p>
              ) : null}

              <div
                className={`${isInitCall ? 'border border-dashed border-gray-50 bg-primary-background-50 p-12-12' : ''} w-full rounded-md`}
              >
                <ConnectMeButton
                  className="w-full"
                  id={connectMeButtonId}
                  metricName={metricName}
                  variant="primary-card"
                  type="button"
                  experience={experience}
                  phoneNumber={phoneNumber}
                  destinationPhoneNumber={connectMeDestinationPhoneNumber}
                  callType={primaryCampaign.callType}
                  placement={primaryCampaign.placement}
                  sessionId={sessionId}
                  product={product}
                  isSendingDisplayMetric={false}
                  onClick={handleOnClickConnectMe}
                  advertiser={primaryCampaign.advertiser}
                >
                  <p className="flex gap-8-8 justify-center items-center mx-auto">
                    <AnimatedPhoneIcon />
                    <span className="mx-auto">Connect Me!</span>
                  </p>
                </ConnectMeButton>
              </div>
            </>
          ) : (
            <>
              <p className="hc-typography--paragraph-md mb-32-32 text-balance">
                To speak with a licensed insurance agent, click{' '}
                <span className="font-bold">{uucButton}</span>
                {isOpen && showCountdown ? (
                  <>
                    {' '}
                    Your spot will go to another caller in{' '}
                    <span className="font-bold">
                      <Countdown
                        initialSeconds={45}
                        onEnd={handleOnCountdownClose}
                      />
                    </span>{' '}
                    seconds.
                  </>
                ) : null}
              </p>

              {secondaryCampaign ? (
                <CallCta
                  id={clickToCallButtonId}
                  phoneCampaign={secondaryCampaign}
                  className="flex w-full flex-wrap items-center justify-center gap-12-12"
                >
                  <p className="flex gap-8-8 justify-center items-center mx-auto">
                    <AnimatedPhoneIcon />
                    <span className="mx-auto">{uucButton}</span>
                  </p>
                </CallCta>
              ) : null}
            </>
          )}

          {secondaryCampaign ? (
            <p className="mt-8-8">
              Or dial at{' '}
              <Link
                className="font-bold"
                href={secondaryCampaign.href}
                id={clickToCallLinkId}
                data-call-call-type={secondaryCampaign.callType}
                data-call-placement={secondaryCampaign.placement}
                data-call-product={secondaryCampaign.product}
                data-call-phone={secondaryCampaign.href}
                data-call-advertiser={secondaryCampaign.advertiser}
              >
                {toUSPhone(secondaryCampaign.phoneNumber)}
              </Link>
            </p>
          ) : null}
        </div>
      </dialog>
    </div>
  );
}
